import { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../services/notifications';

export interface NotificationState {
  isEnabled: boolean;
  permissionStatus: NotificationPermission;
  isRequesting: boolean;
  hasShownBanner: boolean;
  requestNotificationPermission: () => Promise<boolean>;
  markBannerAsShown: () => void;
  resetState: () => void;
  resetNotifications: () => void;
  shouldShowBanner: () => boolean;
}

export const useNotificationState = (): NotificationState => {
  const { isEnabled, getPermissionStatus, requestPermission } = useNotifications();
  const [state, setState] = useState<Omit<NotificationState, 'requestNotificationPermission' | 'markBannerAsShown' | 'resetState' | 'resetNotifications' | 'shouldShowBanner'>>({
    isEnabled: false,
    permissionStatus: 'default',
    isRequesting: false,
    hasShownBanner: false
  });

  // Verificar estado inicial
  useEffect(() => {
    const checkInitialState = () => {
      const permissionStatus = getPermissionStatus();
      const enabled = isEnabled();
      
      setState(prev => ({
        ...prev,
        isEnabled: enabled,
        permissionStatus
      }));
    };

    checkInitialState();
  }, []); // Solo ejecutar una vez al montar el componente

  // Solicitar permisos
  const requestNotificationPermission = useCallback(async () => {
    setState(prev => ({ ...prev, isRequesting: true }));
    
    try {
      const granted = await requestPermission();
      
      if (granted) {
        setState(prev => ({
          ...prev,
          isEnabled: true,
          permissionStatus: 'granted',
          isRequesting: false
        }));
        return true;
      } else {
        setState(prev => ({
          ...prev,
          permissionStatus: 'denied',
          isRequesting: false
        }));
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setState(prev => ({
        ...prev,
        permissionStatus: 'denied',
        isRequesting: false
      }));
      return false;
    }
  }, [requestPermission]);

  // Marcar banner como mostrado
  const markBannerAsShown = useCallback(() => {
    setState(prev => ({ ...prev, hasShownBanner: true }));
  }, []);

  // Resetear estado
  const resetState = useCallback(() => {
    setState({
      isEnabled: false,
      permissionStatus: 'default',
      isRequesting: false,
      hasShownBanner: false
    });
    
    // Limpiar localStorage
    localStorage.removeItem('notification-banner-dismissed');
  }, []);

  // Resetear completamente las notificaciones
  const resetNotifications = useCallback(() => {
    resetState();
    
    // Limpiar todos los datos relacionados
    localStorage.removeItem('notification-banner-dismissed');
    
    // Forzar re-render del banner
    window.location.reload();
  }, [resetState]);

  // Verificar si se debe mostrar el banner
  const shouldShowBanner = useCallback(() => {
    // Solo mostrar si:
    // 1. No hay permisos concedidos
    // 2. No se ha mostrado antes
    // 3. No se han denegado los permisos
    return !state.isEnabled && 
           !state.hasShownBanner && 
           state.permissionStatus !== 'denied';
  }, [state.isEnabled, state.hasShownBanner, state.permissionStatus]);

  return {
    ...state,
    requestNotificationPermission,
    markBannerAsShown,
    resetState,
    resetNotifications,
    shouldShowBanner
  };
};
